import { useEffect, useRef, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { PatternFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import { setUsuario } from "../../Usuario/actions/actions";
import { changePhoto, removeUserPhoto, saveCurrentUser } from "../../Usuario/api-usuario";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import SettingsCard from "../SettingsCard";

const MeusDadosCard = () => {
  const usuario = useSelector((state) => state.usuario);
  const dispatch = useDispatch();
  const hiddenFileInput = useRef(null);
  const [cpfCnpj, setCpfCnpj] = useState(usuario.cpfCnpj || null);
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setCpfCnpj(usuario.cpfCnpj || null);
  }, [usuario.cpfCnpj]);

  const handleUsuarioResponse = (data, mensagemSucesso) => {
    if (!data) return;
    if (data.error) {
      errorMessage(data.message);
      return;
    }
    dispatch(setUsuario(data));
    if (mensagemSucesso) success(mensagemSucesso);
  };

  const handleChangeImage = (event) => {
    const file = event.target.files?.[0];
    // Limpa o input para permitir escolher o mesmo arquivo novamente.
    event.target.value = "";
    if (!file) return;

    const fileFormData = new FormData();
    fileFormData.append("file", file);
    setIsPhotoLoading(true);
    changePhoto(usuario.id, fileFormData)
      .then((data) => handleUsuarioResponse(data, "Foto alterada."))
      .finally(() => setIsPhotoLoading(false));
  };

  const handleRemoveImage = () => {
    setIsPhotoLoading(true);
    removeUserPhoto(usuario.id)
      .then((data) => handleUsuarioResponse(data, "Foto removida."))
      .finally(() => setIsPhotoLoading(false));
  };

  const handleSave = (event) => {
    event.preventDefault();
    setIsSaving(true);
    saveCurrentUser({ ...usuario, cpfCnpj })
      .then((data) => handleUsuarioResponse(data, "Salvo com sucesso."))
      .finally(() => setIsSaving(false));
  };

  const temFoto = Boolean(usuario.fileName);

  return (
    <SettingsCard title="Meus Dados" description="Suas informações pessoais.">
      <Stack spacing={3}>
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Avatar src={temFoto ? usuario.fotoUrl : undefined} alt={usuario.nome} sx={{ width: 80, height: 80 }} />
          <Box minWidth={0}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {usuario.nome}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {usuario.perfil}
            </Typography>
            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              hidden
              ref={hiddenFileInput}
              onChange={handleChangeImage}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhotoCameraOutlinedIcon />}
                disabled={isPhotoLoading}
                onClick={() => hiddenFileInput.current.click()}
              >
                {temFoto ? "Alterar Foto" : "Adicionar Foto"}
              </Button>
              {temFoto && (
                <Button size="small" color="error" disabled={isPhotoLoading} onClick={handleRemoveImage}>
                  Remover
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>

        <Stack component="form" onSubmit={handleSave} direction={{ xs: "column", sm: "row" }} spacing={2}>
          <PatternFormat
            customInput={TextField}
            size="small"
            label="CPF"
            format="###.###.###-##"
            mask="_"
            placeholder="000.000.000-00"
            value={cpfCnpj || ""}
            // Guarda o CPF formatado (como antes) ou null quando vazio, nunca a máscara.
            onValueChange={({ value, formattedValue }) => setCpfCnpj(value ? formattedValue : null)}
            sx={{ minWidth: 220 }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={isSaving}>
              Salvar
            </Button>
          </Box>
        </Stack>
      </Stack>
    </SettingsCard>
  );
};

export default MeusDadosCard;
